import axios from "axios";

// Pick the API base URL with one bulletproof rule:
//   In a browser, ALWAYS use the page's own origin. This keeps
//   session_token cookies on the same domain that the page is served
//   from — which is the only way HttpOnly Secure SameSite=None cookies
//   actually round-trip back on the next request.
//
// The build-time `REACT_APP_BACKEND_URL` is kept as a fallback for
// non-browser environments (SSR / Jest), so test setups stay
// unaffected. In production we ignore it on purpose: if the deploy
// pipeline ever forgets to swap the preview URL into the bundle (as
// happened on leaguehub.co), this guard prevents the entire OAuth
// flow from breaking. Same-origin requests cannot suffer from the
// "cookie set on wrong domain" bug, ever.
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ORIGIN =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : ENV_BACKEND_URL || "";
const BACKEND_URL = ORIGIN || ENV_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ── AI consent interceptor ────────────────────────────────────────────
// Routes under `/api/ai/*` return 403 `AI_CONSENT_REQUIRED` until the
// user has explicitly granted AI processing consent (GDPR Art. 6/9).
// The interceptor catches the 403, dispatches a global event the
// <AiConsentModal /> listens for, awaits the user's decision, and
// transparently retries the original request if they accept.
http.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const orig = error?.config;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || "";
    const isAiConsent =
      status === 403 &&
      typeof detail === "string" &&
      detail.startsWith("AI_CONSENT_REQUIRED");
    if (!isAiConsent || !orig || orig.__lh_consent_retried) {
      return Promise.reject(error);
    }
    orig.__lh_consent_retried = true;
    const result = await new Promise((resolve) => {
      window.dispatchEvent(
        new CustomEvent("lh:ai-consent-required", {
          detail: { onResolve: resolve },
        }),
      );
    });
    if (result?.granted) {
      return http.request(orig);
    }
    return Promise.reject(error);
  },
);

// Slug helpers
export const checkSlug = (slug) =>
  http.get(`/leagues/check-slug`, { params: { slug } }).then((r) => r.data);
export const fetchLeagueBySlug = (slug) =>
  http.get(`/leagues/by-slug/${slug}`).then((r) => r.data);

// Auth
export const fetchMe = () => http.get("/auth/me").then((r) => r.data);
export const exchangeSession = (session_id) =>
  http.post("/auth/session", { session_id }).then((r) => r.data);
export const logout = () => http.post("/auth/logout").then((r) => r.data);

// Profile
export const updateProfile = (patch) => http.patch("/profile", patch).then((r) => r.data);
export const changeDisplayName = (name) =>
  http.patch("/profile/name", { name }).then((r) => r.data);

// ── GDPR / Privacy (Batch B) ──────────────────────────────────────────
// Returns: { granted, version, granted_at, current_version }
export const fetchAiConsent = () =>
  http.get("/account/ai-consent").then((r) => r.data);
export const setAiConsent = (granted) =>
  http.post("/account/ai-consent", { granted: !!granted }).then((r) => r.data);
// JSON dump (Art. 20). Caller picks format via ?fmt=csv for the ZIP.
export const exportAccountJson = () =>
  http.get("/account/export").then((r) => r.data);
export const exportAccountCsvUrl = () => `${API}/account/export?fmt=csv`;
// mode ∈ {anonymize, hard}. confirm MUST be the literal "DELETE".
export const deleteAccount = (mode) =>
  http
    .delete("/account", {
      data: { mode, confirm: "DELETE" },
    })
    .then((r) => r.data);
export const toggleNameHistoryVisibility = (show) =>
  http.patch("/profile/name-history-visibility", { show }).then((r) => r.data);

// Players
export const fetchPlayers = (params) => http.get("/players", { params }).then((r) => r.data);
export const fetchPlayer = (user_id) => http.get(`/players/${user_id}`).then((r) => r.data);
// Public, unauthenticated operator profile resolver — accepts username,
// referral_code, OR a slugified display name. 404 → "Operator not found".
export const fetchPublicOperator = (handle) =>
  http.get(`/public/u/${encodeURIComponent(handle)}`).then((r) => r.data);

// ── Scheduled sessions (scrim/league play calendar) ───────────────────────
export const createSession = (payload) =>
  http.post("/calendar/sessions", payload).then((r) => r.data);
export const fetchMySessions = (days = 60) =>
  http.get(`/calendar/sessions?days=${days}`).then((r) => r.data);
export const fetchSession = (session_id) =>
  http.get(`/calendar/sessions/${session_id}`).then((r) => r.data);
export const inviteToSession = (session_id, user_ids) =>
  http.post(`/calendar/sessions/${session_id}/invites`, { user_ids }).then((r) => r.data);
export const rsvpSession = (session_id, user_id, rsvp) =>
  http.post(`/calendar/sessions/${session_id}/invites/${user_id}/rsvp`, { rsvp }).then((r) => r.data);
export const sessionIcsUrl = (session_id) =>
  `${API}/calendar/sessions/${session_id}.ics`;

// Leagues
export const fetchLeagues = (params) => http.get("/leagues", { params }).then((r) => r.data);
export const fetchMyLeagues = () => http.get("/leagues/mine").then((r) => r.data);
export const fetchLeague = (league_id) => http.get(`/leagues/${league_id}`).then((r) => r.data);
export const createLeague = (body) => http.post("/leagues", body).then((r) => r.data);

// Matchmaking
export const recommendLeagues = (game) =>
  http.post("/matchmaking/recommend", null, { params: game ? { game } : {} }).then((r) => r.data);
export const applyToLeague = (league_id) =>
  http.post("/matchmaking/apply", { league_id }).then((r) => r.data);

// Applications inbox
export const fetchMyApplications = () => http.get("/my/applications").then((r) => r.data);
export const fetchLeagueApplications = (league_id, status) =>
  http.get(`/leagues/${league_id}/applications`, { params: status ? { status } : {} }).then((r) => r.data);
export const decideApplication = (application_id, action) =>
  http.patch(`/applications/${application_id}`, { action }).then((r) => r.data);

// Teams (inside a league)
export const fetchTeams = (league_id) =>
  http.get(`/leagues/${league_id}/teams`).then((r) => r.data);
export const createTeam = (league_id, body) =>
  http.post(`/leagues/${league_id}/teams`, body).then((r) => r.data);
export const fetchAvailablePlayers = (league_id) =>
  http.get(`/leagues/${league_id}/available-players`).then((r) => r.data);
export const fetchUserTeams = (user_id) =>
  http.get(`/users/${user_id}/teams`).then((r) => r.data);
export const removeTeamMember = (team_id, user_id) =>
  http.delete(`/teams/${team_id}/members/${user_id}`).then((r) => r.data);
export const addTeamMember = (team_id, user_id) =>
  http.post(`/teams/${team_id}/members`, { user_id }).then((r) => r.data);
export const updateTeam = (team_id, patch) =>
  http.patch(`/teams/${team_id}`, patch).then((r) => r.data);

// DM threads (inbox)
export const fetchDmThreads = () => http.get("/dms/threads").then((r) => r.data);
export const markDmThreadRead = (user_id) =>
  http.post(`/dms/${user_id}/mark-read`).then((r) => r.data);
export const reactToDm = (message_id, emoji) =>
  http.post(`/dms/messages/${message_id}/react`, { emoji }).then((r) => r.data);

// Inbox — unified pending-decision feed
export const fetchInbox = (status = "pending") =>
  http.get(`/inbox?status=${status}`).then((r) => r.data);
export const fetchInboxCount = () =>
  http.get(`/inbox/count`).then((r) => r.data);
export const decideInbox = (item_id, action) =>
  http.post(`/inbox/${item_id}/decision`, { action }).then((r) => r.data);
// Phase 59 — soft-delete a decided inbox row (hidden_for_user=true).
// The backend refuses if the row is still pending; the UI hides the
// button in that case.
export const deleteInboxMessage = (message_id) =>
  http.delete(`/inbox/${message_id}`).then((r) => r.data);
export const inviteToTeamMailbox = (team_id, user_id) =>
  http.post(`/teams/${team_id}/invite-mailbox`, { user_id }).then((r) => r.data);
export const requestToJoinTeam = (team_id) =>
  http.post(`/teams/${team_id}/request-join`).then((r) => r.data);
export const fetchMyTeamRequestStatus = (team_id) =>
  http.get(`/teams/${team_id}/my-request`).then((r) => r.data);
export const decideReportItem = (item_id, body) =>
  http.post(`/inbox/${item_id}/report-action`, body).then((r) => r.data);

// League BOOST
export const fetchBoostStatus = (league_id) =>
  http.get(`/leagues/${league_id}/boost`).then((r) => r.data);
export const castBoost = (league_id) =>
  http.post(`/leagues/${league_id}/boost`).then((r) => r.data);
export const startSuperboostCheckout = (league_id) =>
  http.post(`/leagues/${league_id}/superboost-checkout`, {
    origin_url: window.location.origin,
  }).then((r) => r.data);

// Admin one-shot referral grants
export const adminCreateGrant = (kind, note) =>
  http.post(`/admin/referral-grants`, { kind, note }).then((r) => r.data);
export const adminListGrants = (status = "all") =>
  http.get(`/admin/referral-grants?status=${status}`).then((r) => r.data);
export const adminRevokeGrant = (token) =>
  http.delete(`/admin/referral-grants/${token}`).then((r) => r.data);

// Vouchers (admin)
export const adminCreateVouchers = (body) =>
  http.post(`/admin/vouchers`, body).then((r) => r.data);
export const adminListVouchers = (params = {}) =>
  http.get(`/admin/vouchers`, { params }).then((r) => r.data);
export const adminRevokeVoucher = (code) =>
  http.post(`/admin/vouchers/${encodeURIComponent(code)}/revoke`).then((r) => r.data);
export const adminDeleteVoucher = (code) =>
  http.delete(`/admin/vouchers/${encodeURIComponent(code)}`).then((r) => r.data);
export const adminVoucherAttribution = (params = {}) =>
  http.get(`/admin/vouchers/attribution`, { params }).then((r) => r.data);

// Voucher redeem (user)
export const redeemVoucher = (code) =>
  http.post(`/vouchers/redeem`, { code }).then((r) => r.data);

// Public: peek + redeem one-shot referral grant
export const peekReferralGrant = (token) =>
  http.get(`/referral/grant/${token}`).then((r) => r.data);
export const redeemReferralGrant = (token) =>
  http.post(`/referral/grant/redeem`, { token }).then((r) => r.data);

// Cancel a paid membership (PRO Player or a specific league's PRO upgrade)
export const cancelMembership = (body = {}) =>
  http.post(`/billing/cancel`, body).then((r) => r.data);

// ── WAVE A ─ Today aggregator + league reads + reactions
export const fetchToday = () => http.get(`/me/today`).then((r) => r.data);
export const markLeagueRead = (league_id) =>
  http.post(`/leagues/${league_id}/mark-read`).then((r) => r.data);
export const reactToMessage = (league_id, message_id, emoji) =>
  http.post(`/leagues/${league_id}/messages/${message_id}/react`, { emoji }).then((r) => r.data);
export const quickScoreMatch = (challenge_id, score_a, score_b) =>
  http.post(`/team-challenges/${challenge_id}/result`, { score_a, score_b }).then((r) => r.data);
// Personal WebSocket URL — frontend builds the wss:// version from REACT_APP_BACKEND_URL.
export const userWsUrl = () => {
  const u = new URL(`${BACKEND_URL}/api/ws/me`);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  return u.toString();
};

// Public landing WebSocket — anonymous, no session required. Streams
// `stats:bump` events so the marketing hero counter can re-animate
// when a new user signs up or a new league is created elsewhere.
export const publicWsUrl = () => {
  const u = new URL(`${BACKEND_URL}/api/ws/public`);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  return u.toString();
};

// ── WAVE B ─ Web Push + Discord notification webhook
export const fetchVapidPublicKey = () =>
  http.get(`/push/vapid-public-key`).then((r) => r.data);
export const subscribePush = (subscription, userAgent) =>
  http.post(`/push/subscribe`, { subscription, user_agent: userAgent }).then((r) => r.data);
export const unsubscribePush = (endpoint) =>
  http.post(`/push/unsubscribe`, { endpoint }).then((r) => r.data);
export const sendPushTest = () => http.post(`/push/test`).then((r) => r.data);
export const setDiscordNotificationWebhook = (url) =>
  http.put(`/profile/discord-notification-webhook`, { url }).then((r) => r.data);
export const testDiscordNotificationWebhook = () =>
  http.post(`/profile/discord-notification-test`).then((r) => r.data);

// ── WAVE C ─ Dispute v2 · Smart scheduling · LFG WS · Recent matches
export const fetchDisputeCategories = () =>
  http.get(`/dispute-categories`).then((r) => r.data);
export const openDisputeV2 = (challenge_id, payload) =>
  http.post(`/team-challenges/${challenge_id}/dispute-v2`, payload).then((r) => r.data);
export const fetchDisputeThread = (challenge_id) =>
  http.get(`/team-challenges/${challenge_id}/dispute`).then((r) => r.data);
export const respondToDispute = (challenge_id, payload) =>
  http.post(`/team-challenges/${challenge_id}/dispute-respond`, payload).then((r) => r.data);
export const suggestMatchTimes = (from_team_id, to_team_id, days_ahead = 14) =>
  http.get(`/team-challenges/suggest-times`, { params: { from_team_id, to_team_id, days_ahead } })
      .then((r) => r.data);
export const fetchMyRecentMatches = (limit = 8) =>
  http.get(`/me/recent-matches`, { params: { limit } }).then((r) => r.data);

// ── WAVE D ─ Premium exports + Season Pass feature flag
export const fetchMyWrapped = (year) =>
  http.get(`/me/wrapped`, { params: year ? { year } : {} }).then((r) => r.data);
export const fetchMatchRecap = (challenge_id) =>
  http.get(`/match-recap/${challenge_id}`).then((r) => r.data);
export const matchesCsvUrl = () => `${BACKEND_URL}/api/exports/matches.csv`;
export const mmrCsvUrl = () => `${BACKEND_URL}/api/exports/mmr-history.csv`;
export const fetchSeasonPassState = () =>
  http.get(`/settings/season-pass`).then((r) => r.data);
export const fetchSeasonThemes = () =>
  http.get(`/settings/season-pass/themes`).then((r) => r.data);
export const adminSetSeasonPassState = (body) =>
  http.put(`/admin/settings/season-pass`, body).then((r) => r.data);

// Season Pass — tiers + XP rules
export const fetchMySeasonPass = () =>
  http.get(`/me/season-pass`).then((r) => r.data);
export const claimSeasonPassTier = (tier, track) =>
  http.post(`/season-pass/claim`, { tier, track }).then((r) => r.data);
export const claimDailySeasonXp = () =>
  http.post(`/season-pass/daily`).then((r) => r.data);
export const fetchSeasonLeaderboard = (limit = 20) =>
  http.get(`/season-pass/leaderboard`, { params: { limit } }).then((r) => r.data);
export const adminGetSeasonPassConfig = () =>
  http.get(`/admin/season-pass/config`).then((r) => r.data);
export const adminPutSeasonPassConfig = (body) =>
  http.put(`/admin/season-pass/config`, body).then((r) => r.data);
export const adminResetSeasonPass = () =>
  http.post(`/admin/season-pass/reset`, { confirm: "RESET" }).then((r) => r.data);

// Admin audit log
export const adminListAudit = (params = {}) =>
  http.get(`/admin/audit`, { params }).then((r) => r.data);

// Rivalries
export const fetchTopRivals = (userId, limit = 5) =>
  http.get(`/players/${userId}/rivals`, { params: { limit } }).then((r) => r.data);
export const fetchRivalry = (teamId, otherTeamId) =>
  http.get(`/teams/${teamId}/rivalry/${otherTeamId}`).then((r) => r.data);

// Onboarding sample league
export const createSampleLeague = () =>
  http.post(`/onboarding/sample-league`, {}).then((r) => r.data);

// Tips
export const createTipCheckout = (recipientUserId, amount, message = "") =>
  http.post(`/tips/checkout`, {
    recipient_user_id: recipientUserId, amount, message,
    origin_url: window.location.origin,
  }).then((r) => r.data);
export const fetchTipsReceived = (limit = 20) =>
  http.get(`/tips/received`, { params: { limit } }).then((r) => r.data);

// On-chain feature flag (Solana / NFT drops / wallet login)
export const fetchOnchainPublic = () =>
  http.get(`/settings/onchain`).then((r) => r.data);
export const adminGetOnchain = () =>
  http.get(`/admin/onchain`).then((r) => r.data);
export const adminSetOnchain = (body) =>
  http.put(`/admin/onchain`, body).then((r) => r.data);

// Landing page — live stats + global search (Cmd-K)
export const fetchLiveStats = () => http.get(`/stats/live`).then((r) => r.data);
export const searchGlobal = (q = "", limit = 8) =>
  http.get(`/search/global`, { params: { q, limit } }).then((r) => r.data);



// Team HQ + rosters
export const fetchTeamHq = (team_id) =>
  http.get(`/teams/${team_id}/hq`).then((r) => r.data);
export const createRoster = (team_id, body) =>
  http.post(`/teams/${team_id}/rosters`, body).then((r) => r.data);
export const updateRoster = (team_id, roster_id, patch) =>
  http.patch(`/teams/${team_id}/rosters/${roster_id}`, patch).then((r) => r.data);
export const deleteRoster = (team_id, roster_id) =>
  http.delete(`/teams/${team_id}/rosters/${roster_id}`).then((r) => r.data);

// HQ identity / captain mgmt / discovery
export const updateTeamHq = (team_id, patch) =>
  http.patch(`/teams/${team_id}`, patch).then((r) => r.data);
export const promoteCaptain = (team_id, user_id) =>
  http.post(`/teams/${team_id}/captains/${user_id}`).then((r) => r.data);
export const demoteCaptain = (team_id, user_id) =>
  http.delete(`/teams/${team_id}/captains/${user_id}`).then((r) => r.data);
export const kickMember = (team_id, user_id) =>
  http.delete(`/teams/${team_id}/members/${user_id}`).then((r) => r.data);

export const fetchHqs = (params = {}) =>
  http.get("/hqs", { params }).then((r) => r.data);
export const fetchFreeAgents = (params = {}) =>
  http.get("/hq/free-agents", { params }).then((r) => r.data);

// Recruitment posts
export const fetchRecruitment = (params = {}) =>
  http.get("/recruitment", { params }).then((r) => r.data);
export const createRecruitment = (body) =>
  http.post("/recruitment", body).then((r) => r.data);
export const closeRecruitment = (post_id) =>
  http.delete(`/recruitment/${post_id}`).then((r) => r.data);

// Standalone team — create without ever joining a league first.
export const createStandaloneTeam = (payload) =>
  http.post("/teams", payload).then((r) => r.data);

// Detach an existing league-attached team to operate as a standalone Team Hub.
export const detachTeamFromLeague = (team_id) =>
  http.post(`/teams/${team_id}/detach-from-league`).then((r) => r.data);

// Request to join a team, optionally tagging which game/title you're applying for.
export const requestJoinTeam = (team_id, body = {}) =>
  http.post(`/teams/${team_id}/request-join`, body).then((r) => r.data);

// Pending join requests for an HQ (shown on the team site for owners)
export const fetchTeamJoinRequests = (team_id) =>
  http.get(`/teams/${team_id}/join-requests`).then((r) => r.data);

// Team availability matrix — captain-facing roster status board.
export const fetchTeamAvailability = (team_id) =>
  http.get(`/teams/${team_id}/availability`).then((r) => r.data);

// Personal availability — patches the current user's profile.
export const setMyAvailability = ({ status, until = null, note = "" }) =>
  http.patch("/profile", {
    availability_status: status || "",
    availability_until:  until || "",
    availability_note:   note || "",
  }).then((r) => r.data);

// Reschedule a team challenge — emits `league.match.rescheduled` webhook.
export const rescheduleChallenge = (challenge_id, scheduled_at) =>
  http.patch(`/team-challenges/${challenge_id}/schedule`, { scheduled_at })
      .then((r) => r.data);

// League announcements — owner posts; everyone reads.
export const postLeagueAnnouncement = (league_id, body) =>
  http.post(`/leagues/${league_id}/announcements`, body).then((r) => r.data);
export const fetchLeagueAnnouncements = (league_id, limit = 20) =>
  http.get(`/leagues/${league_id}/announcements`, { params: { limit } })
      .then((r) => r.data);

// AI banner generation for PRO leagues
export const generateLeagueBanner = (body) =>
  http.post("/ai/league-banner", body, { timeout: 60_000 }).then((r) => r.data);
export const inviteToLeagueMailbox = (league_id, user_id) =>
  http.post(`/leagues/${league_id}/invite-mailbox`, { user_id }).then((r) => r.data);

// Team-vs-team challenges
export const createChallenge = (body) =>
  http.post(`/team-challenges`, body).then((r) => r.data);
export const fetchUpcomingGames = (league_id) =>
  http.get(`/leagues/${league_id}/upcoming-games`).then((r) => r.data);
export const fetchTeamChallenges = (team_id) =>
  http.get(`/teams/${team_id}/challenges`).then((r) => r.data);

// Friends + DMs
export const requestFriend = (user_id) =>
  http.post(`/friends/request`, { user_id }).then((r) => r.data);
export const fetchFriends = () =>
  http.get(`/friends`).then((r) => r.data);
export const fetchFriendStatus = (user_id) =>
  http.get(`/friends/status/${user_id}`).then((r) => r.data);
export const removeFriend = (user_id) =>
  http.delete(`/friends/${user_id}`).then((r) => r.data);
export const fetchDmThread = (user_id) =>
  http.get(`/dms/${user_id}`).then((r) => r.data);
export const sendDm = (user_id, body) =>
  http.post(`/dms/${user_id}`, { body }).then((r) => r.data);
export const createTeamInvite = (team_id) =>
  http.post(`/teams/${team_id}/invites`).then((r) => r.data);
export const peekTeamInvite = (token) =>
  http.get(`/team-invites/${token}`).then((r) => r.data);
export const redeemTeamInvite = (token) =>
  http.post(`/team-invites/${token}/redeem`).then((r) => r.data);

// Tournaments
export const fetchTournaments = (league_id) =>
  http.get(`/leagues/${league_id}/tournaments`).then((r) => r.data);
export const createTournament = (league_id, body) =>
  http.post(`/leagues/${league_id}/tournaments`, body).then((r) => r.data);

// PRO premium config
export const updateLeague = (league_id, patch) =>
  http.patch(`/leagues/${league_id}`, patch).then((r) => r.data);

// Invite tokens (PRO only)
export const createInvite = (league_id) =>
  http.post(`/leagues/${league_id}/invites`).then((r) => r.data);
export const peekInvite = (token) =>
  http.get(`/invites/${token}`).then((r) => r.data);
export const redeemInvite = (token) =>
  http.post(`/invites/${token}/redeem`).then((r) => r.data);

// Roadmap personal mission
export const fetchNextMatch = () =>
  http.get(`/users/me/next-match`).then((r) => r.data);
export const nextMatchIcsUrl = () => `${API}/users/me/next-match.ics`;

// Discord webhook config + announce
export const setDiscordWebhook = (league_id, url) =>
  http.put(`/leagues/${league_id}/discord-webhook`, { url }).then((r) => r.data);
export const announceNextMatch = (league_id) =>
  http.post(`/leagues/${league_id}/announce-next-match`).then((r) => r.data);

// In-league chat
export const fetchMessages = (league_id, after) =>
  http.get(`/leagues/${league_id}/messages`, { params: after ? { after } : {} }).then((r) => r.data);
export const sendMessage = (league_id, body) =>
  http.post(`/leagues/${league_id}/messages`, { body }).then((r) => r.data);

// Team badges (earned via auto-rules)
export const fetchTeamBadges = (team_id) =>
  http.get(`/teams/${team_id}/badges`).then((r) => r.data);

// Brackets / matches
export const fetchMatches = (tournament_id) =>
  http.get(`/tournaments/${tournament_id}/matches`).then((r) => r.data);
export const recordMatchResult = (match_id, score_a, score_b) =>
  http.post(`/matches/${match_id}/result`, { score_a, score_b }).then((r) => r.data);
export const regenerateBracket = (tournament_id) =>
  http.post(`/tournaments/${tournament_id}/regenerate`).then((r) => r.data);
export const switchTournamentStyle = (tournament_id, style) =>
  http.post(`/tournaments/${tournament_id}/style`, { style }).then((r) => r.data);

// Invite a player to a league
export const invitePlayer = (league_id, user_id) =>
  http.post(`/leagues/${league_id}/invite`, { user_id }).then((r) => r.data);

// LFG
export const fetchLfg = (params) => http.get("/lfg", { params }).then((r) => r.data);
export const createLfg = (body) => http.post("/lfg", body).then((r) => r.data);
export const deleteLfg = (post_id) => http.delete(`/lfg/${post_id}`).then((r) => r.data);

// Catalog
export const fetchGames = () => http.get("/games").then((r) => r.data);
export const createGame = (body) => http.post("/games", body).then((r) => r.data);
export const searchIgdbGames = (q, limit = 8) =>
  http.get("/games/search", { params: { q, limit } }).then((r) => r.data);
export const fetchLiveStreamers = (limit = 12) =>
  http.get("/public/live-streamers", { params: { limit } }).then((r) => r.data);
export const fetchBadges = () => http.get("/badges").then((r) => r.data);
export const fetchPlans = () => http.get("/plans").then((r) => r.data);
export const fetchCatalog = () => http.get("/catalog").then((r) => r.data);

// AI / billing
export const generateAiAvatar = (prompt, style) =>
  http.post("/ai/avatar", { prompt, style }).then((r) => r.data);
export const upgradePlan = (plan) =>
  http.post("/billing/upgrade", { plan }).then((r) => r.data);
export const devUpgradePlan = (plan) =>
  http.post("/billing/dev-upgrade", { plan }).then((r) => r.data);
export const createCheckout = (package_id, origin_url, extra = {}) =>
  http.post("/billing/checkout", { package_id, origin_url, ...extra }).then((r) => r.data);

// True monthly Stripe Subscription (auto-renewal) for pro_player / pro_league
export const createSubscription = (package_id, origin_url, extra = {}) =>
  http.post("/billing/subscribe", { package_id, origin_url, ...extra }).then((r) => r.data);

// Re-arm auto-renewal on a Stripe sub that's pending cancel
export const reactivateSubscription = () =>
  http.post("/billing/reactivate").then((r) => r.data);
export const getCheckoutStatus = (session_id) =>
  http.get(`/billing/checkout/${session_id}`).then((r) => r.data);

// Redeem an admin-minted referral code (gift PRO / promo flow).
export const redeemReferralCode = (code) =>
  http.post("/referral/redeem-code", { code }).then((r) => r.data);
export const giftProPlayer = (recipient_user_id, origin_url) =>
  http.post("/billing/gift-pro", { recipient_user_id, origin_url }).then((r) => r.data);

// Public league ladder + tiers
export const fetchLeagueLadder = (slug) =>
  http.get(`/leagues/by-slug/${slug}/ladder`).then((r) => r.data);
export const fetchTiers = () =>
  http.get("/tiers").then((r) => r.data);

// Admin — match disputes queue
export const adminListDisputes = (status = "open") =>
  http.get("/admin/disputes", { params: { status } }).then((r) => r.data);
export const adminDisputesCount = () =>
  http.get("/admin/disputes/count").then((r) => r.data);
export const adminResolveDispute = (challenge_id, note = "") =>
  http.post(`/admin/challenges/${challenge_id}/resolve-dispute`, { note }).then((r) => r.data);
export const adminForceReverse = (challenge_id, body) =>
  http.post(`/admin/challenges/${challenge_id}/admin-reverse`, body).then((r) => r.data);

// Team HQ chat
export const fetchTeamMessages = (team_id, after) =>
  http.get(`/teams/${team_id}/messages`, { params: after ? { after } : {} }).then((r) => r.data);
export const sendTeamMessage = (team_id, body) =>
  http.post(`/teams/${team_id}/messages`, { body }).then((r) => r.data);

// Tryouts
export const createTryout = (team_id, body) =>
  http.post(`/teams/${team_id}/tryouts`, body).then((r) => r.data);
export const fetchTeamTryouts = (team_id) =>
  http.get(`/teams/${team_id}/tryouts`).then((r) => r.data);
export const browseTryouts = (params = {}) =>
  http.get("/tryouts", { params }).then((r) => r.data);
export const closeTryout = (tryout_id) =>
  http.delete(`/tryouts/${tryout_id}`).then((r) => r.data);
export const registerForTryout = (tryout_id, note) =>
  http.post(`/tryouts/${tryout_id}/register`, { note }).then((r) => r.data);
export const fetchTryoutRegistrations = (tryout_id) =>
  http.get(`/tryouts/${tryout_id}/registrations`).then((r) => r.data);
export const rateTryoutRegistration = (tryout_id, user_id, rating, note) =>
  http.post(`/tryouts/${tryout_id}/registrations/${user_id}/rate`, { rating, note }).then((r) => r.data);
export const decideTryoutRegistration = (tryout_id, user_id, action) =>
  http.post(`/tryouts/${tryout_id}/registrations/${user_id}/decide`, { action }).then((r) => r.data);
export const fetchMyTryoutRegistrations = () =>
  http.get(`/users/me/tryout-registrations`).then((r) => r.data);

// Team disband (owner only) — cascades cleanup across rosters/chats/tryouts/inbox.
export const disbandTeam = (team_id) =>
  http.delete(`/teams/${team_id}`).then((r) => r.data);

// Friends — block / unblock (list + remove already exported above)
export const blockUser = (user_id) =>
  http.post(`/blocks/${user_id}`).then((r) => r.data);
export const unblockUser = (user_id) =>
  http.delete(`/blocks/${user_id}`).then((r) => r.data);
export const fetchBlocks = () =>
  http.get("/blocks").then((r) => r.data);

// Reports — file + list reasons + my history
export const fetchReportReasons = () =>
  http.get("/reports/reasons").then((r) => r.data);
export const fileReport = (reported_user_id, reason, details, opts = {}) =>
  http.post("/reports", {
    reported_user_id, reason, details,
    image_kind: opts.image_kind || undefined,
    target_team_id: opts.target_team_id || undefined,
  }).then((r) => r.data);
export const fetchMyReports = () =>
  http.get("/reports/mine").then((r) => r.data);

// Admin — login/logout/me + reports moderation + league moderation
export const adminLogin = (password) =>
  http.post("/admin/login", { password }).then((r) => r.data);
export const adminLogout = () =>
  http.post("/admin/logout").then((r) => r.data);
export const adminMe = () =>
  http.get("/admin/me").then((r) => r.data);
// One-shot ops — wipe cached Stripe price IDs after flipping live mode.
export const adminWipeBillingPlans = () =>
  http.post("/admin/ops/wipe-billing-plans").then((r) => r.data);
// Go-live cleanup: wipe seed leagues + orphan bots. Two-stage:
// 1) cleanup-go-live with body={} → dry-run preview.
// 2) cleanup-go-live with body={confirm: true} → actually delete.
export const adminCleanupGoLive = (confirm = false) =>
  http.post("/admin/ops/cleanup-go-live", { confirm }).then((r) => r.data);

// Admin — users + tier promotions
export const adminSearchUsers = (q = "", limit = 20) =>
  http.get("/admin/users/search", { params: { q, limit } }).then((r) => r.data);
export const adminPromoteTier = (user_id, tier, duration_days, reason) =>
  http.post(`/admin/users/${user_id}/promote-tier`, { tier, duration_days, reason }).then((r) => r.data);
export const adminRevokeTier = (user_id) =>
  http.post(`/admin/users/${user_id}/revoke-tier`).then((r) => r.data);

// Public feature flags (frontend gates Tip button + Wallet via this).
export const fetchFeatureFlags = () =>
  http.get("/feature-flags").then((r) => r.data);

// Top Tipped Operators leaderboard (gated by tips_enabled).
export const fetchTipsLeaderboard = (window = "week", limit = 10) =>
  http.get("/leaderboards/tips", { params: { window, limit } }).then((r) => r.data);

// Player wallet
export const fetchMyWallet = () =>
  http.get("/wallet/me").then((r) => r.data);
export const submitWithdrawal = (method, destination, amount) =>
  http.post("/wallet/withdraw", { method, destination, amount }).then((r) => r.data);

// Admin — creator payouts
export const adminPayoutsSettings = () =>
  http.get("/admin/payouts/settings").then((r) => r.data);
export const adminTogglePayouts = (enabled) =>
  http.post("/admin/payouts/toggle", { enabled }).then((r) => r.data);
export const adminSetUserSplit = (user_id, pct) =>
  http.post(`/admin/payouts/user/${user_id}/split`, { pct }).then((r) => r.data);
export const adminListWithdrawals = (status) =>
  http.get("/admin/payouts/withdrawals", { params: { status } }).then((r) => r.data);
export const adminDecideWithdrawal = (withdrawal_id, decision, note) =>
  http.post(`/admin/payouts/withdrawals/${withdrawal_id}/decide`, { decision, note }).then((r) => r.data);
export const adminListReports = (status = "open") =>
  http.get("/admin/reports", { params: { status } }).then((r) => r.data);
export const adminDecideReport = (report_id, action, note) =>
  http.post(`/admin/reports/${report_id}/decide`, { action, note }).then((r) => r.data);

// Support tickets (logged-in users + admin)
export const listMySupportTickets = () =>
  http.get("/support/tickets").then((r) => r.data);
export const createSupportTicket = ({ category, subject, body }) =>
  http.post("/support/tickets", { category, subject, body }).then((r) => r.data);
export const adminListTickets = (params = {}) => {
  // Back-compat: callers used to pass a string status; normalise to object.
  const opts = typeof params === "string" ? { status: params } : params;
  return http.get("/admin/tickets", { params: opts }).then((r) => r.data);
};
export const adminPatchTicket = (ticket_id, patch) =>
  http.patch(`/admin/tickets/${ticket_id}`, patch).then((r) => r.data);
export const adminReplyTicket = (ticket_id, message, admin_handle) =>
  http.post(`/admin/tickets/${ticket_id}/reply`, { message, admin_handle }).then((r) => r.data);
export const adminTicketDetail = (ticket_id) =>
  http.get(`/admin/tickets/${ticket_id}`).then((r) => r.data);
export const adminTicketStats = () =>
  http.get("/admin/tickets/stats").then((r) => r.data);
export const adminTicketCannedReplies = () =>
  http.get("/admin/tickets/canned-replies").then((r) => r.data);
export const adminClaimTicket = (ticket_id, admin_handle) =>
  http.post(`/admin/tickets/${ticket_id}/claim`, { admin_handle }).then((r) => r.data);
export const adminReleaseTicket = (ticket_id) =>
  http.post(`/admin/tickets/${ticket_id}/claim`, { release: true }).then((r) => r.data);
export const adminReopenTicket = (ticket_id) =>
  http.post(`/admin/tickets/${ticket_id}/reopen`).then((r) => r.data);
export const adminRestorePlayer = (user_id) =>
  http.post(`/admin/players/${user_id}/restore`).then((r) => r.data);
export const adminListLeagues = (q) =>
  http.get("/admin/leagues", { params: q ? { q } : {} }).then((r) => r.data);
export const adminDissolveLeague = (league_id) =>
  http.delete(`/admin/leagues/${league_id}`).then((r) => r.data);
export const adminTransferLeagueOwnership = (league_id, new_owner_user_id) =>
  http.post(`/admin/leagues/${league_id}/transfer-ownership`, { new_owner_user_id }).then((r) => r.data);

// Phase 54 — Operator power tools (pause / broadcast / analytics / export / clone)
export const adminPauseLeague = (league_id, paused, reason) =>
  http.post(`/admin/leagues/${league_id}/pause`, { paused, reason }).then((r) => r.data);
export const adminBroadcast = ({ title, body, scope, scope_league_id, scope_user_ids, link_url }) =>
  http.post(`/admin/broadcast`, {
    title, body, scope, scope_league_id, scope_user_ids, link_url,
  }).then((r) => r.data);
export const adminAnalytics = () =>
  http.get(`/admin/analytics`).then((r) => r.data);
export const exportLeague = (league_id, format = "json") =>
  http.post(`/leagues/${league_id}/export`, null, {
    params: { format },
    responseType: format === "csv" ? "blob" : "json",
  }).then((r) => r.data);
export const cloneLeague = (league_id, { name, slug } = {}) =>
  http.post(`/leagues/${league_id}/clone`, { name, slug }).then((r) => r.data);

// Phase 59 — League/team role management. Used by the (forthcoming)
// "Manage staff" panels in LeagueOwnerSettings + TeamHq.
export const listLeagueRoles = (league_id) =>
  http.get(`/leagues/${league_id}/roles`).then((r) => r.data);
export const setLeagueRole = (league_id, user_id, role) =>
  http.post(`/leagues/${league_id}/roles/${user_id}`, { role }).then((r) => r.data);
export const clearLeagueRole = (league_id, user_id) =>
  http.delete(`/leagues/${league_id}/roles/${user_id}`).then((r) => r.data);

export const listTeamRoles = (team_id) =>
  http.get(`/teams/${team_id}/roles`).then((r) => r.data);
export const setTeamRole = (team_id, user_id, role) =>
  http.post(`/teams/${team_id}/roles/${user_id}`, { role }).then((r) => r.data);
export const clearTeamRole = (team_id, user_id) =>
  http.delete(`/teams/${team_id}/roles/${user_id}`).then((r) => r.data);

export const setTeamMemberStatus = (team_id, user_id, status) =>
  http.post(`/teams/${team_id}/members/${user_id}/status`, { status }).then((r) => r.data);

// Phase 60 — attach a standalone team to a league.
export const attachTeamToLeague = (team_id, league_id) =>
  http.post(`/teams/${team_id}/attach-to-league/${league_id}`).then((r) => r.data);
export const listUserTeams = (user_id) =>
  http.get(`/users/${user_id}/teams`).then((r) => r.data);

// Phase 60 — streams + clips
export const listLeagueStreams = (league_id) =>
  http.get(`/leagues/${league_id}/streams`).then((r) => r.data);
export const listLeagueActiveChallenges = (league_id) =>
  http.get(`/leagues/${league_id}/active-challenges`).then((r) => r.data);
export const upsertLeagueStream = (league_id, body) =>
  http.post(`/leagues/${league_id}/streams`, body).then((r) => r.data);
export const endLeagueStream = (league_id, challenge_id) =>
  http.delete(`/leagues/${league_id}/streams/${challenge_id}`).then((r) => r.data);
export const listLeagueClips = (league_id, challenge_id) =>
  http.get(`/leagues/${league_id}/clips`, { params: challenge_id ? { challenge_id } : {} }).then((r) => r.data);
export const createLeagueClip = (league_id, body) =>
  http.post(`/leagues/${league_id}/clips`, body).then((r) => r.data);
export const deleteLeagueClip = (league_id, clip_id) =>
  http.delete(`/leagues/${league_id}/clips/${clip_id}`).then((r) => r.data);
// Aggregator: every clip across the leagues you're in (CLIPS dashboard block).
export const fetchMyClips = (limit = 24) =>
  http.get("/me/clips", { params: { limit } }).then((r) => r.data);

// Phase 61 — Stripe Connect Express (creator payouts auto-paid via Stripe).
export const startConnectOnboarding = (origin_url) =>
  http.post("/payouts/connect/account", { origin_url }).then((r) => r.data);
export const fetchConnectStatus = () =>
  http.get("/payouts/connect/status").then((r) => r.data);
export const fetchConnectLoginLink = () =>
  http.post("/payouts/connect/login-link").then((r) => r.data);

// Phase 59 Phase B — league-scoped dispute moderation
export const listLeagueDisputes = (league_id, status = "open") =>
  http.get(`/leagues/${league_id}/disputes`, { params: { status } }).then((r) => r.data);
export const dismissLeagueDispute = (league_id, challenge_id, note) =>
  http.post(`/leagues/${league_id}/disputes/${challenge_id}/dismiss`, { note }).then((r) => r.data);
export const voidLeagueDispute = (league_id, challenge_id, note) =>
  http.post(`/leagues/${league_id}/disputes/${challenge_id}/void`, { note }).then((r) => r.data);
export const flipLeagueDispute = (league_id, challenge_id, body) =>
  http.post(`/leagues/${league_id}/disputes/${challenge_id}/flip`, body).then((r) => r.data);

// League ownership / manager / upgrade (member-paid)
export const transferLeagueOwnership = (league_id, new_owner_user_id) =>
  http.post(`/leagues/${league_id}/transfer-ownership`, { new_owner_user_id }).then((r) => r.data);
export const setLeagueManager = (league_id, manager_user_id) =>
  http.post(`/leagues/${league_id}/manager`, { manager_user_id }).then((r) => r.data);
export const deleteLeague = (league_id) =>
  http.delete(`/leagues/${league_id}`).then((r) => r.data);
export const resetSandbox = (league_id) =>
  http.post(`/leagues/${league_id}/reset-sandbox`).then((r) => r.data);
export const upgradeLeagueToPro = (league_id, origin_url) =>
  http.post(`/billing/subscribe`, {
    package_id: "pro_league", origin_url, league_id,
  }).then((r) => r.data);

// League → HQ invitation
export const inviteHqToLeague = (league_id, team_id) =>
  http.post(`/leagues/${league_id}/invite-hq`, { team_id }).then((r) => r.data);

// Match result recording (writes score_events for leaderboards)
export const recordChallengeResult = (challenge_id, payload) =>
  http.post(`/team-challenges/${challenge_id}/result`, payload).then((r) => r.data);

// Onboarding
export const fetchOnboardingState = () =>
  http.get("/onboarding/state").then((r) => r.data);
export const markOnboardingStep = (step) =>
  http.post("/onboarding/step", { step }).then((r) => r.data);
export const dismissOnboarding = () =>
  http.post("/onboarding/dismiss").then((r) => r.data);

// Notifications
export const fetchUnreadNotifications = () =>
  http.get("/notifications/unread").then((r) => r.data);
export const markNotificationRead = (notif_id) =>
  http.post(`/notifications/${notif_id}/read`).then((r) => r.data);
export const markAllNotificationsRead = () =>
  http.post("/notifications/mark-all-read").then((r) => r.data);

// League members (for @mention autocomplete)
export const fetchLeagueMembers = (league_id) =>
  http.get(`/leagues/${league_id}/members`).then((r) => r.data);

// Phase 35 — combat stats + tier progress + smart league finder
export const fetchMyStats = (league_id) =>
  http.get("/me/stats", { params: league_id ? { league_id } : {} }).then((r) => r.data);
export const fetchPlayerStats = (user_id, league_id) =>
  http.get(`/players/${user_id}/stats`, { params: league_id ? { league_id } : {} }).then((r) => r.data);
export const fetchMyProgress = () =>
  http.get("/me/progress").then((r) => r.data);
export const fetchSmartLeagues = (filters = {}) =>
  http.get("/leagues", { params: filters }).then((r) => r.data);

// Phase 36 — clutch moments · role suggestions · PRO heatmap
export const fetchRoleSuggestions = (game) =>
  http.get("/roles", { params: game ? { game } : {} }).then((r) => r.data);
export const fetchClutchMoments = (params = {}) =>
  http.get("/clutch-moments", { params }).then((r) => r.data);
export const fetchLeagueHeatmap = (league_id, weeks = 8) =>
  http.get(`/leagues/${league_id}/heatmap`, { params: { weeks } }).then((r) => r.data);

// Leaderboards
export const fetchLeaderboard = (params = {}) =>
  http.get("/leaderboards", { params }).then((r) => r.data);
export const fetchLeaderboardGames = () =>
  http.get("/leaderboards/games").then((r) => r.data);
export const fetchLeaderboardArchive = (params = {}) =>
  http.get("/leaderboards/archive", { params }).then((r) => r.data);

// Join an OPEN team — bypasses invite flow when team has `is_open` set.
export const joinOpenTeam = (team_id) =>
  http.post(`/teams/${team_id}/join-open`).then((r) => r.data);

// League ↔ League DMs (owner-only)
export const fetchLeagueDmThreads = (league_id) =>
  http.get(`/leagues/${league_id}/dm/threads`).then((r) => r.data);
export const fetchLeagueDmThread = (from_id, to_id) =>
  http.get(`/leagues/${from_id}/dm/${to_id}`).then((r) => r.data);
export const sendLeagueDm = (from_id, to_id, body) =>
  http.post(`/leagues/${from_id}/dm/${to_id}`, { body }).then((r) => r.data);

// Analytics
export const fetchAnalytics = () => http.get("/analytics").then((r) => r.data);

// Contact form
export const submitContact = (payload) =>
  http.post("/contact", payload).then((r) => r.data);

// Glory Badges (50-achievement system)
export const fetchGloryBadgeCatalog = () =>
  http.get("/glory-badges/catalog").then((r) => r.data);
export const fetchMyGloryBadges = () =>
  http.get("/glory-badges/me").then((r) => r.data);
export const fetchPlayerGloryBadges = (user_id) =>
  http.get(`/glory-badges/users/${user_id}`).then((r) => r.data);

// Referral program (Free → 10 onboarding-complete sign-ups → 30 days PRO)
export const fetchMyReferral = () =>
  http.get("/referral/me").then((r) => r.data);
export const claimReferral = (code) =>
  http.post("/referral/claim", { code }).then((r) => r.data);
export const resolveReferralCode = (code) =>
  http.get(`/referral/resolve/${code}`).then((r) => r.data);

// Backend redirect URL for Discord auth (full URL, browser navigates here)
export const discordLoginUrl = `${API}/auth/discord/login`;
